import { numberToWords } from "@/Helpers/format-helper";
import { cn } from "@/lib/utils";
import { Input } from "@/shadcnComponents/ui/input";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/shadcnComponents/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/shadcnComponents/ui/select";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/shadcnComponents/ui/calendar";
import mixpanel from "mixpanel-browser";
import { format } from "date-fns";
import { Button } from "@/shadcnComponents/ui/button";
import { CampaignFormField, ToolTipInfo } from "../Components/Components";
import {
  FieldAndPlaceHolderStyles,
  goalList,
  platformList,
  brandTypeList,
} from "../Helpers/Helper";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import { Label } from "@/shadcnComponents/ui/label";
import { ArrowRight, IndianRupeeIcon as Rupee } from "lucide-react";
import { useNewCampaign } from "../CustomHooks/NewCampaignHooks";

export const CampaignInfoForm = () => {
  const {
    formData,
    masterData,
    selectedPurpose,
    setSelectedPurpose,
    handleSelectChange,
    handleInputChange,
    handleBudgetChange,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    setOpenReviewPopup,
  } = useNewCampaign();
  return (
    <>
      <div className="flex flex-col gap-4">
        <Card className="h-auto p-4">
          <CardHeader className="p-0 pb-4">
            <CardTitle className="text-sm font-semibold">
              Campaign Name
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Input
              value={formData?.campaignInfo?.campaignName}
              onChange={(e) =>
                handleInputChange(e, "campaignInfo", "campaignName")
              }
              placeholder="Enter campaign name"
            />
          </CardContent>
        </Card>
        <Card className="p-4">
          <CardHeader className="p-0 pb-4">
            <CardTitle className="text-sm font-semibold text-blue-500">
              Campaign Info
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="campaign-goal" className="text-sm font-medium">
                Campaign Goal
              </Label>
              <p className="text-xs text-gray-500">
                Define what you want to achieve with this campaign
              </p>
              <div className="flex justify-between w-full gap-3">
                {masterData?.CampaignPurpose?.map((option) => (
                  <Button
                    key={option?._id}
                    variant={
                      selectedPurpose?.includes(option?._id)
                        ? "outline"
                        : "outline"
                    }
                    className={`flex flex-col items-center p-2 gap-[6px] py-3 h-auto flex-1 ${
                      selectedPurpose?.includes(option?._id)
                        ? "bg-[#F1F1F1] border-black"
                        : ""
                    }`}
                    onClick={() =>
                      setSelectedPurpose((prev) =>
                        prev.includes(option?._id)
                          ? prev.filter((item) => item !== option?._id)
                          : [...prev, option?._id]
                      )
                    }
                  >
                    <span>
                      <img
                        className="w-6 h-6 mb-1 "
                        src={option?.CollabGoalsIcon}
                        alt=""
                      />{" "}
                    </span>
                    <span className="text-xs">{option.CollabGoalsName}</span>
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  label: "Measured Metric",
                  subheading:
                    "Select the key metric to measure your campaign's performance",
                  options: goalList,
                  name: "collabGoals",
                  defaultValue: formData.collabInfo.collabGoals,
                  value: formData.collabInfo.collabGoals,
                  onValueChange: (value) =>
                    handleSelectChange("collabGoals", "collabInfo", value),
                },
                {
                  label: "Social Platform",
                  subheading:
                    "Choose the platform where you want to reach your audience",
                  options: platformList,
                  name: "collabPlatform",
                  defaultValue: formData.collabInfo.collabPlatform,
                  value: formData.collabInfo.collabPlatform,
                  onValueChange: (value) =>
                    handleSelectChange("collabPlatform", "collabInfo", value),
                },
                {
                  label: "Industry Type",
                  subheading:
                    "Specify your industry to help influencers align their content with your business context",
                  name: "collabIndustry",
                  value: formData.collabInfo.collabIndustry,
                  onValueChange: (e) =>
                    handleInputChange(e, "collabInfo", "collabIndustry"),
                },
                {
                  label: "Business Type",
                  subheading:
                    "If you cater to a mixed customer segment, select the targeted one for this campaign",
                  options: brandTypeList,
                  name: "collabBuisnessType",
                  defaultValue: formData.collabInfo.collabBuisnessType,
                  value: formData.collabInfo.collabBuisnessType,
                  onValueChange: (value) =>
                    handleSelectChange(
                      "collabBuisnessType",
                      "collabInfo",
                      value
                    ),
                },
              ].map(
                ({
                  label,
                  subheading,
                  options,
                  name,
                  defaultValue,
                  value,
                  onValueChange,
                }) => (
                  <div key={label} className="space-y-2">
                    <Label
                      htmlFor={label.toLowerCase().replace(" ", "-")}
                      className="text-sm font-medium"
                    >
                      {label}
                    </Label>
                    <p className="text-xs text-gray-500">{subheading}</p>
                    {options ? (
                      <Select
                        name={name}
                        defaultValue={defaultValue}
                        value={value}
                        onValueChange={onValueChange}
                      >
                        <SelectTrigger
                          id={label.toLowerCase().replace(" ", "-")}
                        >
                          <SelectValue
                            placeholder={`Select ${label.toLowerCase()}`}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {options.map((option) => (
                            <SelectItem
                              key={option}
                              value={option.toLowerCase()}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={value}
                        onChange={onValueChange}
                        name={name}
                        id={label.toLowerCase().replace(" ", "-")}
                        placeholder={`Enter ${label.toLowerCase()}`}
                      />
                    )}
                  </div>
                )
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="p-4">
          <CardHeader className="p-0 pb-4">
            <CardTitle className="text-sm font-semibold text-blue-500 flex items-center gap-2">
              <Rupee className="w-5 h-5" />
              Budget and Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 space-y-4">
            <div className="flex  gap-4">
              <div className="flex flex-col w-full justify-around space-y-2">
                <Label htmlFor="min-budget" className="text-sm font-medium">
                  Min Campaign Budget (in INR)
                </Label>
                <p className="text-xs text-gray-500">
                  Enter the minimum amount allocated for this campaign's budget
                </p>
                <Input
                  className={FieldAndPlaceHolderStyles}
                  name="minBillAmount"
                  value={formData.Budget.minBillAmount}
                  min={10000}
                  max={1000000}
                  step={1000}
                  onChange={(e) =>
                    handleBudgetChange(
                      parseFloat(e.target.value),
                      "minBillAmount"
                    )
                  }
                  id="budget"
                  placeholder="Enter your budget"
                  type="number"
                />
                <span className="text-sm">{`${numberToWords(
                  formData?.Budget.minBillAmount || 0
                )} Rupees`}</span>
              </div>
              <div className="flex flex-col w-full justify-around space-y-2">
                <Label htmlFor="max-budget" className="text-sm font-medium">
                  Max Campaign Budget (in INR)
                </Label>
                <p className="text-xs text-gray-500">
                  Enter the maximum amount you are willing to allocate for this
                  campaign's budget
                </p>
                <Input
                  className={FieldAndPlaceHolderStyles}
                  name="budget"
                  value={formData.Budget.maxBillAmount}
                  min={10000}
                  max={1000000}
                  step={1000}
                  onChange={(e) =>
                    handleBudgetChange(
                      parseFloat(e.target.value),
                      "maxBillAmount"
                    )
                  }
                  id="budget"
                  placeholder="Enter your budget"
                  type="number"
                />
                <span className="text-sm">{`${numberToWords(
                  formData?.Budget.maxBillAmount || 0
                )} Rupees`}</span>
              </div>
            </div>
            <div className="flex   gap-4">
              <div className="flex w-full gap-2">
                <CampaignFormField
                  labelHead="Select Start Date"
                  labelHeadRightIcon={
                    <ToolTipInfo
                      Content={
                        <div className="flex flex-col items-center text-start max-w-sm text-wrap">
                          This marks the earliest day influencers are expected
                          to go live. Custom live dates for each influencer will
                          be set later.
                        </div>
                      }
                    />
                  }
                  Children={
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-[280px] justify-start text-left font-normal",
                            !startDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? (
                            format(startDate, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          className="bg-white rounded-lg shadow-lg"
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          disabled={(date) => {
                            const currentDate = new Date();
                            const minDate = new Date(
                              currentDate.getTime() + 168 * 60 * 60 * 1000
                            ); // 168 hours ahead
                            const parsedEndDate = endDate
                              ? new Date(endDate)
                              : null;

                            return (
                              date <= minDate ||
                              (parsedEndDate && date >= parsedEndDate) ||
                              (parsedEndDate &&
                                date >=
                                  new Date(
                                    parsedEndDate.getTime() -
                                      168 * 60 * 60 * 1000
                                  ))
                            );
                          }}
                          initialFocus
                          defaultMonth={
                            new Date(
                              new Date().getFullYear(),
                              new Date(
                                new Date(Date.now()).getTime() +
                                  168 * 60 * 60 * 1000
                              ).getMonth(),
                              1
                            )
                          }
                        />
                      </PopoverContent>
                    </Popover>
                  }
                  mandatory={true}
                />
                <CampaignFormField
                  labelHead="Select End Date"
                  labelHeadRightIcon={
                    <ToolTipInfo
                      Content={
                        <div className="flex flex-col items-center text-start max-w-sm text-wrap">
                          Choose the date when the campaign duration ends.
                          Influencers are expected to go live within this time
                          range.
                        </div>
                      }
                    />
                  }
                  Children={
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-[280px] justify-start text-left font-normal",
                            !endDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? (
                            format(endDate, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          className="bg-white rounded-lg shadow-lg"
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          initialFocus
                          disabled={(date) =>
                            date < new Date() ||
                            date < new Date(startDate) ||
                            date <=
                              new Date(
                                new Date(Date.now()).getTime() +
                                  168 * 60 * 60 * 1000
                              ) ||
                            date <=
                              new Date(
                                new Date(startDate).getTime() +
                                  48 * 60 * 60 * 1000
                              )
                          }
                          defaultMonth={
                            new Date(
                              new Date(startDate).getFullYear(),
                              new Date(
                                new Date(startDate).getTime() +
                                  48 * 60 * 60 * 1000
                              ).getMonth(),
                              1
                            )
                          }
                        />
                      </PopoverContent>
                    </Popover>
                  }
                  mandatory={true}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="mt-6 flex justify-end">
          <Button
            className="flex items-center gap-2"
            style={{ backgroundColor: "#DC2627" }}
            onClick={() => {
              mixpanel.track(
                "Brand Draft Campaign - Campaign Info Section: Save and Continue - clicked"
              );
              setOpenReviewPopup(true);
            }}
          >
            Save & Continue
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  );
};
